/**
 * TestPromptCard Component
 * Displays a test prompt result with score, regenerate and accept buttons
 */

import { RefreshCw, Check, User } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TestResult } from "../types";

interface TestPromptCardProps {
  test: TestResult;
  onRegenerate: () => void;
  onAccept: () => void;
}

export function TestPromptCard({
  test,
  onRegenerate,
  onAccept,
}: TestPromptCardProps) {
  const scoreColor =
    test.score >= 85
      ? "text-green-500"
      : test.score >= 75
        ? "text-amber-500"
        : "text-red-500";
  const barColor =
    test.score >= 85
      ? "bg-green-500"
      : test.score >= 75
        ? "bg-amber-500"
        : "bg-red-500";

  return (
    <div
      className={cn(
        "rounded-lg border overflow-hidden transition-all",
        test.accepted
          ? "border-green-500/40 bg-green-500/5"
          : "border-white/10 bg-[#1a1a1a]",
      )}
    >
      {/* Image area */}
      <div className="relative aspect-[4/3] bg-gradient-to-br from-white/5 to-white/[0.02]">
        {/* Placeholder - would be actual generated image */}
        <div className="absolute inset-0 flex items-center justify-center">
          <User className="w-12 h-12 text-white/20" />
        </div>

        {/* Test number badge */}
        <div className="absolute top-3 left-3 px-2 py-1 rounded bg-black/70 backdrop-blur-sm">
          <span className="text-[10px] font-semibold tracking-wider text-white/80">
            TEST PROMPT {String(test.promptNumber).padStart(2, "0")}
          </span>
        </div>

        {/* Accepted badge */}
        {test.accepted && (
          <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
            <Check className="w-4 h-4 text-white" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Prompt text */}
        <p className="text-xs text-muted-foreground line-clamp-2 mb-4">
          &ldquo;{test.prompt}&rdquo;
        </p>

        {/* Score bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
              Likeness Score
            </span>
            <span className={cn("text-sm font-mono font-medium", scoreColor)}>
              {test.score}
            </span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className={cn("h-full rounded-full transition-all", barColor)}
              style={{ width: `${test.score}%` }}
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onRegenerate}
            disabled={test.accepted}
            className={cn(
              "flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg",
              "text-xs font-medium transition-all",
              test.accepted
                ? "bg-white/5 text-muted-foreground/40 cursor-not-allowed"
                : "bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-foreground",
            )}
          >
            <RefreshCw className="w-3 h-3" />
            Regenerate
          </button>
          <button
            type="button"
            onClick={onAccept}
            disabled={test.accepted}
            className={cn(
              "flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg",
              "text-xs font-medium transition-all",
              test.accepted
                ? "bg-green-500/20 text-green-500 cursor-not-allowed"
                : "bg-primary/10 text-primary hover:bg-primary/20",
            )}
          >
            <Check className="w-3 h-3" />
            {test.accepted ? "Accepted" : "Accept"}
          </button>
        </div>
      </div>
    </div>
  );
}
